import Swiper from 'swiper'
import { Autoplay, Controller, EffectCreative, EffectFade, Keyboard, Navigation, Pagination, Parallax } from 'swiper/modules'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { destroySwiper } from '../utils/dom'

gsap.registerPlugin(ScrollTrigger)

let activeSwipers = []
let carouselController = null

const keyboardOptions = {
	enabled: true,
	onlyInViewport: true,
}

function formatNumber(number) {
	return ('0' + number).slice(-2)
}

function createSwiper(container, options) {
	if (!container || !container.querySelector('.swiper-slide')) {
		return null
	}

	destroySwiper(container)

	const instance = new Swiper(container, options)
	activeSwipers.push(instance)

	return instance
}

function updateFloorPlanTabs(tabs, title, activeIndex) {
	const activeTab = tabs[activeIndex]

	if (title && activeTab) {
		title.textContent = activeTab.dataset.label || ''
	}

	tabs.forEach((tab, index) => {
		const isActive = index === activeIndex

		tab.classList.toggle('is-active', isActive)
		tab.setAttribute('aria-pressed', isActive ? 'true' : 'false')
		tab.tabIndex = isActive ? 0 : -1
	})
}

function moveFloorPlanTab(tabs, currentIndex, direction) {
	const lastIndex = tabs.length - 1

	if (direction === 'first') return 0
	if (direction === 'last') return lastIndex
	if (direction === 'next') return currentIndex === lastIndex ? 0 : currentIndex + 1
	if (direction === 'prev') return currentIndex === 0 ? lastIndex : currentIndex - 1

	return currentIndex
}

export default function initCarousel() {
	activeSwipers.forEach((swiperInstance) => {
		if (!swiperInstance.destroyed) {
			swiperInstance.destroy(true, true)
		}
	})
	activeSwipers = []

	carouselController?.abort()
	carouselController = new AbortController()
	const { signal } = carouselController

	// MARK Hero Carousels
	const heroCarousels = document.querySelectorAll('.hero__media-carousel')
	heroCarousels.forEach((carousel) => {
		const shouldAutoplay = carousel.querySelectorAll('.swiper-slide').length > 1

		createSwiper(carousel, {
			modules: [Autoplay, EffectFade],
			autoplay: shouldAutoplay,
			allowTouchMove: shouldAutoplay,
			effect: 'fade',
			fadeEffect: {
				crossFade: true,
			},
		})
	})

	// MARK: Galleries
	const articleGalleries = document.querySelectorAll('.gallery')
	articleGalleries.forEach((gallery) => {
		const galleryContainer = gallery.querySelector('.gallery__carousel')

		createSwiper(galleryContainer, {
			modules: [EffectFade, Keyboard, Navigation, Pagination],
			autoHeight: true,
			keyboard: keyboardOptions,
			loop: true,
			speed: 600,
			effect: 'fade',
			fadeEffect: {
				crossFade: true,
			},
			navigation: {
				nextEl: gallery.querySelector('.gallery__next'),
				prevEl: gallery.querySelector('.gallery__prev'),
			},
			pagination: {
				el: gallery.querySelector('.gallery__pagination'),
				type: 'fraction',
				formatFractionCurrent: function (number) {
					return formatNumber(number)
				},
				formatFractionTotal: function (number) {
					return formatNumber(number)
				},
			},
			on: {
				slideChangeTransitionEnd: function () {
					ScrollTrigger.refresh()
				},
			},
		})
	})

	// MARK: Card Carousels
	const cardCarousels = document.querySelectorAll('.card-carousel')
	cardCarousels.forEach((carousel) => {
		const prev = carousel.querySelector('.card-carousel__prev')
		const next = carousel.querySelector('.card-carousel__next')

		createSwiper(carousel, {
			modules: [Keyboard, Navigation],
			keyboard: keyboardOptions,
			slidesPerView: 1.1,
			spaceBetween: 20,
			speed: 600,
			navigation: {
				nextEl: next,
				prevEl: prev,
			},
			breakpoints: {
				600: {
					slidesPerView: 1.55,
				},
				800: {
					slidesPerView: 2,
				},
				1000: {
					slidesPerView: 2.33,
				},
				1200: {
					slidesPerView: 2.67,
				},
				1400: {
					slidesPerView: 3,
				},
			},
		})
	})

	// MARK: Reviews Carousels
	const reviewsCarousels = document.querySelectorAll('.reviews-carousel')
	reviewsCarousels.forEach((carousel) => {
		const container = carousel.querySelector('.reviews-carousel__container')
		const prev = carousel.querySelector('.reviews-carousel__prev')
		const next = carousel.querySelector('.reviews-carousel__next')
		const pagination = carousel.querySelector('.reviews-carousel__pagination')

		createSwiper(container, {
			modules: [EffectCreative, Keyboard, Navigation, Pagination],
			keyboard: keyboardOptions,
			loop: true,
			slidesPerView: 1,
			spaceBetween: 0,
			speed: 600,
			watchSlidesProgress: true,
			effect: 'creative',
			creativeEffect: {
				prev: {
					translate: ['-60px', 0, 0],
					opacity: 0,
				},
				next: {
					translate: ['60px', 0, 0],
					opacity: 0,
				},
			},
			navigation: {
				nextEl: next,
				prevEl: prev,
			},
			pagination: {
				el: pagination,
				type: 'fraction',
				formatFractionCurrent: function (number) {
					return formatNumber(number)
				},
				formatFractionTotal: function (number) {
					return formatNumber(number)
				},
			},
		})
	})

	// MARK: Timeline Carousels
	const timelineCarousels = document.querySelectorAll('.timeline')
	timelineCarousels.forEach((carousel) => {
		const container = carousel.querySelector('.timeline__carousel')
		const prev = carousel.querySelector('.timeline__prev')
		const next = carousel.querySelector('.timeline__next')

		createSwiper(container, {
			modules: [Keyboard, Navigation, Parallax],
			keyboard: keyboardOptions,
			slidesPerView: 1,
			spaceBetween: 20,
			speed: 600,
			slideToClickedSlide: true,
			parallax: true,
			navigation: {
				nextEl: next,
				prevEl: prev,
			},
		})
	})

	// MARK: Floor Plan Carousels
	const floorPlans = document.querySelectorAll('.floor-plans')

	floorPlans.forEach((container) => {
		const carousel = container.querySelector('.floor-plans-carousel')
		const title = container.querySelector('.floor-plans-title')
		const tabs = Array.from(container.querySelectorAll('.floor-plans-tab'))

		if (!carousel || !tabs.length) return

		// Initialise Carousel
		const floorPlansSwiper = createSwiper(carousel, {
			modules: [EffectFade, Keyboard],
			speed: 600,
			allowTouchMove: false,
			autoHeight: true,
			effect: 'fade',
			keyboard: keyboardOptions,
			fadeEffect: {
				crossFade: true,
			},
			on: {
				afterInit: (swiperInstance) => {
					updateFloorPlanTabs(tabs, title, swiperInstance.activeIndex)
				},
				activeIndexChange: (swiperInstance) => {
					updateFloorPlanTabs(tabs, title, swiperInstance.activeIndex)
				},
				slideChangeTransitionEnd: function () {
					ScrollTrigger.refresh()
				},
			},
		})

		if (!floorPlansSwiper) return

		updateFloorPlanTabs(tabs, title, floorPlansSwiper.activeIndex)

		// Tabs Navigation
		tabs.forEach((button, tabIndex) => {
			const index = Number(button.dataset.slide || tabIndex)

			button.addEventListener('click', () => {
				floorPlansSwiper.slideTo(index, 600, true)
			}, { signal })

			button.addEventListener('keydown', (event) => {
				let direction = null

				if (event.key === 'ArrowRight' || event.key === 'ArrowDown') direction = 'next'
				if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') direction = 'prev'
				if (event.key === 'Home') direction = 'first'
				if (event.key === 'End') direction = 'last'

				if (!direction) return

				event.preventDefault()

				const nextIndex = moveFloorPlanTab(tabs, tabIndex, direction)
				const nextTab = tabs[nextIndex]

				if (!nextTab) return

				nextTab.focus()
				floorPlansSwiper.slideTo(Number(nextTab.dataset.slide || nextIndex), 600, true)
			}, { signal })
		})

	})

	// MARK: News Carousels
	const newsCarousels = document.querySelectorAll('.news-carousel')
	newsCarousels.forEach((carousel) => {
		const slider = carousel.querySelector('.news-carousel__slider')

		createSwiper(slider, {
			modules: [Keyboard, Navigation],
			keyboard: keyboardOptions,
			slidesPerView: 1.1,
			spaceBetween: 24,
			navigation: {
				nextEl: carousel.querySelector('.news-carousel__next'),
				prevEl: carousel.querySelector('.news-carousel__prev'),
			},
			breakpoints: {
				800: {
					slidesPerView: 1.5,
				},
				1200: {
					slidesPerView: 2,
				},
				1600: {
					slidesPerView: 3,
				},
			},
		})
	})

	// MARK: Events Carousels
	const eventsCarousels = document.querySelectorAll('.events-carousel')
	eventsCarousels.forEach((carousel) => {
		const mediaCarousel = carousel.querySelector('.events-media-carousel')
		const contentCarousel = carousel.querySelector('.events-content-carousel')
		const pagination = carousel.querySelector('.events-carousel__pagination')
		const paginationDesktop = carousel.querySelector('.events-carousel__pagination-desktop')
		const paginationMobile = carousel.querySelector('.events-carousel__pagination-mobile')
		const progressPath = carousel.querySelector('.events-carousel__progress-current-path')
		const shouldLoop = contentCarousel?.querySelectorAll('.swiper-slide').length > 1

		if (pagination && paginationDesktop && paginationMobile) {
			const mediaQuery = window.matchMedia('(max-width: 959px)')
			const movePagination = (event) => {
				const target = event.matches ? paginationMobile : paginationDesktop
				target.appendChild(pagination)
			}

			movePagination(mediaQuery)
			mediaQuery.addEventListener('change', movePagination, { signal })
		}

		let circumference = 0
		if (progressPath) {
			circumference = 2 * Math.PI * 40
			progressPath.style.strokeDasharray = circumference
			progressPath.style.strokeDashoffset = circumference
		}

		const mediaSwiper = createSwiper(mediaCarousel, {
			modules: [EffectCreative],
			allowTouchMove: false,
			loop: shouldLoop,
			speed: 600,
			watchSlidesProgress: true,
			effect: 'creative',
			creativeEffect: {
				limitProgress: 1,
				prev: { opacity: 0 },
				next: { opacity: 0 },
			},
		})

		createSwiper(contentCarousel, {
			modules: [Autoplay, Controller, EffectCreative, Keyboard, Navigation, Pagination],
			keyboard: keyboardOptions,
			loop: shouldLoop,
			autoplay: shouldLoop ? { delay: 5000 } : false,
			speed: 600,
			watchSlidesProgress: true,
			effect: 'creative',
			creativeEffect: {
				prev: { translate: ['-60px', 0, 0], opacity: 0 },
				next: { translate: ['60px', 0, 0], opacity: 0 },
			},
			controller: {
				control: mediaSwiper,
			},
			navigation: {
				nextEl: carousel.querySelector('.events-carousel__next'),
				prevEl: carousel.querySelector('.events-carousel__prev'),
			},
			pagination: {
				el: pagination,
				type: 'fraction',
				formatFractionCurrent: formatNumber,
				formatFractionTotal: formatNumber,
			},
			on: {
				autoplayTimeLeft(swiper, time, progress) {
					if (progressPath && circumference > 0) {
						progressPath.style.strokeDashoffset = circumference * progress
					}
				},
			},
		})
	})
}
